import { Fragment, useCallback, useMemo, useState } from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import ActionsButton from '../../toolbar/components/Actions.component';
import { Form } from '../../form/Form';
import ConfirmDialog from '../../common/Confirm.component';
import ConfirmButton from '../../actions/ConfirmButton';
import DrawerComponent from '../../common/Drawer.component';
import ItemAdd from '../create';
import PaperTypography from '../../common/PaperTypography.component';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';
import Alert from '../../common/Alert.component';
import EditIcon from '@mui/icons-material/Edit';
import { HEIGHT_TABLE_ITEMS } from '../../../module/listItem/conf/Data.conf';
import TableItemsEditable from '../tableEditable/TableItemsEditable.component';

const TabUpdateItemStatus = ({
  dataGrouped,
  setSuccess = () => {},
  module,
}) => {
  const { action, query } = module;
  const { jwt } = useSelector((state) => state.login);

  const [checked, setChecked] = useState(null);
  const [showAction, setShowAction] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(null);
  const [error, setError] = useState(false);
  const [openRows, setOpenRows] = useState({'NEW ITEM':true});

  const productsList = useMemo(
    () => _.flatMap(checked, (group) => _.keys(_.pickBy(group, Boolean))),
    [checked]
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onOpen = useCallback(
    (data) => {
      setOpen(true);
      setValues(data);
    },
    [setOpen]
  );

  const onSelectItem = ({ id, status, available }) => {
    setChecked({
      ...checked,
      [available]: {
        ...checked?.[available],
        [id]: status,
      },
    });
  };

  const onSelectAll = (key, products) => {
    const numSelected = _.keys(_.pickBy(checked?.[key], _.identity))?.length;
    const isChecked =
      (products?.length > 0 && products?.length === numSelected) || false;
    setChecked({
      ...checked,
      [key]: products?.reduce((acc, item) => {
        acc[item?.id] = !isChecked;
        return acc;
      }, {}),
    });
  };

  const onSubmit = useCallback(() => {
    fetchAPI(
      query.action.settings,
      {},
      '',
      { ids: productsList, available: values?.available?.id },
      jwt
    )
      .then(() => setSuccess((prev) => !prev))
      .catch((err) => setError(err));
  }, [values, productsList, query, jwt, setSuccess]);

  const toggleRow = (rowId) => {
    setOpenRows((prev) => ({ ...prev, [rowId]: !openRows?.[rowId] || false }));
  };

  return (
    <Fragment>
      {error && (
        <Alert severity='error'>
          {error?.response?.data?.error?.message || 'An error has occurred'}
        </Alert>
      )}

      <Stack direction={'row'} spacing={2} ml={2}>
        <Box mb={2}>
          <ItemAdd
            setSuccessAdd={setSuccess}
            module={{
              ...module,
              create: { conf: module.create.conf.default },
            }}
            isModal={true}
            viewIconAdd={true}
          />
        </Box>

        {!_.some(dataGrouped, (obj) =>
          _.some(_.values(obj), (val) => val !== undefined)
        ) && (
          <Box width={'95%'}>
            <PaperTypography
              title={'No items have been added to this section'}
            />
          </Box>
        )}
        {productsList?.length !== 0 && (
          <DrawerComponent
            button={
              <Box sx={{ display: 'flex' }} mb={2} ml={2}>
                <ActionsButton
                  onClick={() => setShowAction(!showAction)}
                  showAction={showAction}
                  endIcon={<EditIcon />}
                  variant='contained'
                />
              </Box>
            }
          >
            <Box>
              <Typography mb={5} variant='h4'>
                Update all items selected
              </Typography>
              <Form
                fields={action?.conf?.fields}
                schema={action?.conf?.schema}
                defaultValues={action?.conf?.defaultValues}
                onSubmit={onOpen}
              >
                <Stack direction={'row'} spacing={1} sx={{ mt: 1, mb: 1 }}>
                  <ConfirmButton />

                  <ConfirmDialog
                    open={open}
                    onClose={onClose}
                    title='Are you sure you want to update all rows selected?'
                    description='This action is permanently. You cannot undo this action.'
                    cancelAction={onClose}
                    confirmAction={onSubmit}
                    color='info'
                    label='Confirm'
                  />
                </Stack>
              </Form>
            </Box>
          </DrawerComponent>
        )}
      </Stack>

      <Grid container mt={2} spacing={5}>
        {dataGrouped?.map((item) => {
          const key = _.head(Object.keys(item));
          const dataItem = item?.[key];
          const id = `tobedelivred-${key}`;
          return (
            dataItem && (
              <Grid item xs={12} key={id}>
                <TableItemsEditable
                  data={dataItem}
                  setSuccessEdit={setSuccess}
                  onSelectItem={onSelectItem}
                  checked={checked?.[key]}
                  onSelectAll={() => onSelectAll(key, dataItem)}
                  isSelectable={true}
                  height={HEIGHT_TABLE_ITEMS}
                  label={key}
                  module={module}
                  toggleRow={toggleRow}
                  open={openRows?.[key]}
                />
              </Grid>
            )
          );
        })}
      </Grid>
    </Fragment>
  );
};

export default TabUpdateItemStatus;
